import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { 
  Trophy, 
  Medal, 
  Award, 
  Download, 
  FileSpreadsheet, 
  AlertCircle, 
  Calendar, 
  Users, 
  Layers
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

interface MedalRequirement {
  category: string;
  categoryId: string;
  ageGroup: string;
  ageGroupId: string;
  gender: string;
  eventCount: number;
  participantCount: number;
  firstPlaceMedals: number;
  secondPlaceMedals: number;
  thirdPlaceMedals: number;
  participationMedals: number;
}

interface CategorySummary {
  categoryId: string;
  name: string;
  eventCount: number;
  firstPlaceMedals: number;
  secondPlaceMedals: number;
  thirdPlaceMedals: number;
  participationMedals: number;
}

interface AgeGroupSummary {
  ageGroupId: string;
  name: string;
  eventCount: number;
  firstPlaceMedals: number;
  secondPlaceMedals: number;
  thirdPlaceMedals: number;
  participationMedals: number;
}

interface ChampionRequirement {
  gender: string;
  ageGroup: string;
  ageGroupId: string;
  count: number;
}

const MedalRequirementsPage: React.FC = () => {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [medalRequirements, setMedalRequirements] = useState<MedalRequirement[]>([]);
  const [categorySummaries, setCategorySummaries] = useState<CategorySummary[]>([]);
  const [ageGroupSummaries, setAgeGroupSummaries] = useState<AgeGroupSummary[]>([]);
  const [championRequirements, setChampionRequirements] = useState<ChampionRequirement[]>([]);
  const [totalMedals, setTotalMedals] = useState({
    firstPlace: 0,
    secondPlace: 0,
    thirdPlace: 0,
    participation: 0,
    champions: 0
  });

  // Generate an array of the last 5 years and next 5 years for the dropdown
  const currentYear = new Date().getFullYear();
  const yearsArray = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

  useEffect(() => {
    checkAuthorization();
  }, []);

  useEffect(() => {
    if (isAuthorized) {
      calculateMedalRequirements();
    }
  }, [selectedYear, isAuthorized]);

  const checkAuthorization = async () => {
    try {
      const { data: userRoles } = await supabase
        .from('user_roles')
        .select(`
          roles!inner(
            role_name
          )
        `)
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

      const hasAccess = userRoles?.some(ur => 
        ur.roles.role_name === 'admin' || 
        ur.roles.role_name === 'manager'
      ) ?? false;

      setIsAuthorized(hasAccess);
      if (!hasAccess) {
        navigate('/');
      }
    } catch (error) {
      console.error('Error checking authorization:', error);
      setError('Failed to verify access permissions');
    }
  };

  const calculateMedalRequirements = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all competitions for the selected year
      const { data: competitions, error: competitionsError } = await supabase
        .from('events')
        .select(`
          event_id,
          event_name,
          gender_category,
          is_team_event,
          category_id,
          age_group_id,
          category:sports_categories(
            category_id,
            category_name
          ),
          age_group:age_groups(
            age_group_id,
            group_name,
            birth_years
          )
        `)
        .eq('competition_year', selectedYear);

      if (competitionsError) throw competitionsError;

      // Fetch all registrations for the selected year
      const { data: registrations, error: registrationsError } = await supabase
        .from('student_event_registrations')
        .select(`
          registration_id,
          event_id,
          student_id,
          status
        `)
        .eq('competition_year', selectedYear)
        .in('status', ['registered', 'confirmed']);

      if (registrationsError) throw registrationsError;

      // Calculate medal requirements
      const requirements: MedalRequirement[] = [];
      const categoryMap = new Map<string, CategorySummary>();
      const ageGroupMap = new Map<string, AgeGroupSummary>();
      const championMap = new Map<string, ChampionRequirement>();

      // Process each competition
      competitions?.forEach(competition => {
        const categoryId = competition.category_id;
        const categoryName = competition.category?.category_name || 'Unknown';
        const ageGroupId = competition.age_group_id;
        const ageGroupName = competition.age_group?.group_name || 'Unknown';
        const gender = competition.gender_category;
        
        // Count participants for this competition
        const eventRegistrations = registrations?.filter(reg => 
          reg.event_id === competition.event_id
        ) || [];
        
        const participantCount = eventRegistrations.length;
        
        // Calculate medals needed
        const firstPlaceMedals = 1; // Always 1 gold medal per event
        const secondPlaceMedals = 1; // Always 1 silver medal per event
        const thirdPlaceMedals = 1; // Always 1 bronze medal per event
        const participationMedals = Math.max(0, participantCount - 3); // Participation medals for those not in top 3
        
        // Add to requirements
        requirements.push({
          category: categoryName,
          categoryId,
          ageGroup: ageGroupName,
          ageGroupId,
          gender: gender === 'M' ? 'Male' :
                  gender === 'F' ? 'Female' : 'All',
          eventCount: 1,
          participantCount,
          firstPlaceMedals,
          secondPlaceMedals,
          thirdPlaceMedals,
          participationMedals
        });
        
        // Update category summary
        if (!categoryMap.has(categoryId)) {
          categoryMap.set(categoryId, {
            categoryId,
            name: categoryName,
            eventCount: 0,
            firstPlaceMedals: 0,
            secondPlaceMedals: 0,
            thirdPlaceMedals: 0,
            participationMedals: 0
          });
        }
        const categorySummary = categoryMap.get(categoryId)!;
        categorySummary.eventCount += 1;
        categorySummary.firstPlaceMedals += firstPlaceMedals;
        categorySummary.secondPlaceMedals += secondPlaceMedals;
        categorySummary.thirdPlaceMedals += thirdPlaceMedals;
        categorySummary.participationMedals += participationMedals;
        
        // Update age group summary
        if (ageGroupId && !ageGroupMap.has(ageGroupId)) {
          ageGroupMap.set(ageGroupId, {
            ageGroupId,
            name: ageGroupName,
            eventCount: 0,
            firstPlaceMedals: 0,
            secondPlaceMedals: 0,
            thirdPlaceMedals: 0,
            participationMedals: 0
          });
        }
        
        if (ageGroupId) {
          const ageGroupSummary = ageGroupMap.get(ageGroupId)!;
          ageGroupSummary.eventCount += 1;
          ageGroupSummary.firstPlaceMedals += firstPlaceMedals;
          ageGroupSummary.secondPlaceMedals += secondPlaceMedals;
          ageGroupSummary.thirdPlaceMedals += thirdPlaceMedals;
          ageGroupSummary.participationMedals += participationMedals;
        }
        
        // Track potential champions (by age group and gender)
        if (ageGroupId) {
          const championKey = `${gender}-${ageGroupId}`;
          if (!championMap.has(championKey)) {
            championMap.set(championKey, {
              gender: gender === 'M' ? 'Male' : gender === 'F' ? 'Female' : 'All',
              ageGroup: ageGroupName,
              ageGroupId,
              count: 0
            });
          }
        }
      });
      
      // Calculate champions - estimate 1 champion per gender per age group
      // This is a simplification - in reality, champions are determined by who wins in multiple categories
      const championRequirements: ChampionRequirement[] = [];
      championMap.forEach((requirement, key) => {
        // Only consider Male and Female categories for champions (not 'All')
        if (requirement.gender !== 'All') {
          requirement.count = 1; // Estimate 1 champion per gender per age group
          championRequirements.push(requirement);
        }
      });
      
      // Calculate totals
      const totalFirstPlace = requirements.reduce((sum, req) => sum + req.firstPlaceMedals, 0);
      const totalSecondPlace = requirements.reduce((sum, req) => sum + req.secondPlaceMedals, 0);
      const totalThirdPlace = requirements.reduce((sum, req) => sum + req.thirdPlaceMedals, 0);
      const totalParticipation = requirements.reduce((sum, req) => sum + req.participationMedals, 0);
      const totalChampions = championRequirements.reduce((sum, req) => sum + req.count, 0);
      
      setMedalRequirements(requirements);
      setCategorySummaries(Array.from(categoryMap.values()));
      setAgeGroupSummaries(Array.from(ageGroupMap.values()));
      setChampionRequirements(championRequirements);
      setTotalMedals({
        firstPlace: totalFirstPlace,
        secondPlace: totalSecondPlace,
        thirdPlace: totalThirdPlace,
        participation: totalParticipation,
        champions: totalChampions
      });
      
    } catch (error: any) {
      console.error('Error calculating medal requirements:', error);
      setError(error.message || 'Failed to calculate medal requirements');
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    try {
      // Create workbook with multiple sheets
      const workbook = XLSX.utils.book_new();
      
      // Summary sheet
      const summaryData = [
        [getTranslatedText('Medal Requirements Summary', 'Medaljebehovsoversikt', 'பதக்க தேவைகள் சுருக்கம்'), '', '', ''],
        [getTranslatedText('Year', 'År', 'ஆண்டு'), selectedYear.toString(), '', ''],
        ['', '', '', ''],
        [getTranslatedText('Medal Type', 'Medaljetype', 'பதக்க வகை'), getTranslatedText('Count', 'Antall', 'எண்ணிக்கை'), '', ''],
        [getTranslatedText('First Place Medals (Gold)', 'Førsteplass medaljer (Gull)', 'முதல் இடம் பதக்கங்கள் (தங்கம்)'), totalMedals.firstPlace, '', ''],
        [getTranslatedText('Second Place Medals (Silver)', 'Andreplass medaljer (Sølv)', 'இரண்டாம் இடம் பதக்கங்கள் (வெள்ளி)'), totalMedals.secondPlace, '', ''],
        [getTranslatedText('Third Place Medals (Bronze)', 'Tredjeplass medaljer (Bronse)', 'மூன்றாம் இடம் பதக்கங்கள் (வெண்கலம்)'), totalMedals.thirdPlace, '', ''],
        [getTranslatedText('Participation Medals', 'Deltakelsesmedaljer', 'பங்கேற்பு பதக்கங்கள்'), totalMedals.participation, '', ''],
        [getTranslatedText('Champion Trophies', 'Mestertrofeer', 'சாம்பியன் கோப்பைகள்'), totalMedals.champions, '', ''],
        [getTranslatedText('Total Medals', 'Totalt antall medaljer', 'மொத்த பதக்கங்கள்'), totalMedals.firstPlace + totalMedals.secondPlace + totalMedals.thirdPlace + totalMedals.participation, '', '']
      ];
      
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, getTranslatedText('Summary', 'Sammendrag', 'சுருக்கம்'));
      
      // Detailed requirements sheet
      const detailData = [
        [
          getTranslatedText('Category', 'Kategori', 'பிரிவு'), 
          getTranslatedText('Age Group', 'Aldersgruppe', 'வயது பிரிவு'), 
          getTranslatedText('Gender', 'Kjønn', 'பாலினம்'), 
          getTranslatedText('Events', 'Hendelser', 'நிகழ்வுகள்'), 
          getTranslatedText('Participants', 'Deltakere', 'பங்கேற்பாளர்கள்'), 
          getTranslatedText('Gold', 'Gull', 'தங்கம்'), 
          getTranslatedText('Silver', 'Sølv', 'வெள்ளி'), 
          getTranslatedText('Bronze', 'Bronse', 'வெண்கலம்'), 
          getTranslatedText('Participation', 'Deltakelse', 'பங்கேற்பு')
        ]
      ];
      
      medalRequirements.forEach(req => {
        detailData.push([
          req.category,
          req.ageGroup,
          getTranslatedGender(req.gender),
          req.eventCount,
          req.participantCount,
          req.firstPlaceMedals,
          req.secondPlaceMedals,
          req.thirdPlaceMedals,
          req.participationMedals
        ]);
      });
      
      const detailSheet = XLSX.utils.aoa_to_sheet(detailData);
      XLSX.utils.book_append_sheet(workbook, detailSheet, getTranslatedText('Detailed Requirements', 'Detaljerte krav', 'விரிவான தேவைகள்'));
      
      // Category summary sheet
      const categoryData = [
        [
          getTranslatedText('Category', 'Kategori', 'பிரிவு'), 
          getTranslatedText('Events', 'Hendelser', 'நிகழ்வுகள்'), 
          getTranslatedText('Gold', 'Gull', 'தங்கம்'), 
          getTranslatedText('Silver', 'Sølv', 'வெள்ளி'), 
          getTranslatedText('Bronze', 'Bronse', 'வெண்கலம்'), 
          getTranslatedText('Participation', 'Deltakelse', 'பங்கேற்பு')
        ]
      ];
      
      categorySummaries.forEach(cat => {
        categoryData.push([
          cat.name,
          cat.eventCount,
          cat.firstPlaceMedals,
          cat.secondPlaceMedals,
          cat.thirdPlaceMedals,
          cat.participationMedals
        ]);
      });
      
      const categorySheet = XLSX.utils.aoa_to_sheet(categoryData);
      XLSX.utils.book_append_sheet(workbook, categorySheet, getTranslatedText('By Category', 'Etter kategori', 'பிரிவு வாரியாக'));
      
      // Age group summary sheet
      const ageGroupData = [
        [
          getTranslatedText('Age Group', 'Aldersgruppe', 'வயது பிரிவு'), 
          getTranslatedText('Events', 'Hendelser', 'நிகழ்வுகள்'), 
          getTranslatedText('Gold', 'Gull', 'தங்கம்'), 
          getTranslatedText('Silver', 'Sølv', 'வெள்ளி'), 
          getTranslatedText('Bronze', 'Bronse', 'வெண்கலம்'), 
          getTranslatedText('Participation', 'Deltakelse', 'பங்கேற்பு')
        ]
      ];
      
      ageGroupSummaries.forEach(age => {
        ageGroupData.push([
          age.name,
          age.eventCount,
          age.firstPlaceMedals,
          age.secondPlaceMedals,
          age.thirdPlaceMedals,
          age.participationMedals
        ]);
      });
      
      const ageGroupSheet = XLSX.utils.aoa_to_sheet(ageGroupData);
      XLSX.utils.book_append_sheet(workbook, ageGroupSheet, getTranslatedText('By Age Group', 'Etter aldersgruppe', 'வயது பிரிவு வாரியாக'));
      
      // Champions sheet
      const championsData = [
        [
          getTranslatedText('Age Group', 'Aldersgruppe', 'வயது பிரிவு'), 
          getTranslatedText('Gender', 'Kjønn', 'பாலினம்'), 
          getTranslatedText('Champion Trophies', 'Mestertrofeer', 'சாம்பியன் கோப்பைகள்')
        ]
      ];
      
      championRequirements.forEach(champ => {
        championsData.push([
          champ.ageGroup,
          getTranslatedGender(champ.gender),
          champ.count
        ]);
      });
      
      const championsSheet = XLSX.utils.aoa_to_sheet(championsData);
      XLSX.utils.book_append_sheet(workbook, championsSheet, getTranslatedText('Champions', 'Mestere', 'சாம்பியன்கள்'));
      
      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `medal_requirements_${selectedYear}.xlsx`);
      
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      setError('Failed to export data to Excel');
    }
  };

  // Helper function to get translated text based on current language
  const getTranslatedText = (en: string, nn: string, ta: string): string => {
    if (i18n.language === 'ta') return ta;
    if (i18n.language === 'nn') return nn;
    return en;
  };

  // Helper function to translate gender
  const getTranslatedGender = (gender: string): string => {
    if (gender === 'Male') {
      return getTranslatedText('Male', 'Gutt', 'ஆண்');
    } else if (gender === 'Female') {
      return getTranslatedText('Female', 'Jente', 'பெண்');
    } else {
      return getTranslatedText('All', 'Alle', 'அனைத்தும்');
    }
  };

  // Language selector component
  const LanguageSelector = () => (
    <div className="flex space-x-2">
      <button
        onClick={() => i18n.changeLanguage('en')}
        className={`px-2 py-1 rounded text-sm ${i18n.language === 'en' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
      >
        EN
      </button>
      <button
        onClick={() => i18n.changeLanguage('nn')}
        className={`px-2 py-1 rounded text-sm ${i18n.language === 'nn' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
      >
        NN
      </button>
      <button
        onClick={() => i18n.changeLanguage('ta')}
        className={`px-2 py-1 rounded text-sm ${i18n.language === 'ta' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
      >
        TA
      </button>
    </div>
  );

  if (!isAuthorized) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">
          {getTranslatedText(
            'Medal Requirements Calculator',
            'Medaljebehovskalkulator',
            'பதக்க தேவைகள் கணிப்பான்'
          )}
        </h1>
        <div className="flex space-x-3">
          <LanguageSelector />
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
          >
            {yearsArray.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          
          <button
            onClick={exportToExcel}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            {getTranslatedText('Export to Excel', 'Eksporter til Excel', 'Excel-க்கு ஏற்றுமதி செய்')}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg shadow p-6">
              <div className="flex items-center">
                <Medal className="h-10 w-10 text-yellow-500 mr-3" />
                <div>
                  <p className="text-sm font-medium text-yellow-800">
                    {getTranslatedText('Gold Medals', 'Gullmedaljer', 'தங்கப் பதக்கங்கள்')}
                  </p>
                  <p className="text-2xl font-bold text-yellow-900">{totalMedals.firstPlace}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg shadow p-6">
              <div className="flex items-center">
                <Medal className="h-10 w-10 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-800">
                    {getTranslatedText('Silver Medals', 'Sølvmedaljer', 'வெள்ளிப் பதக்கங்கள்')}
                  </p>
                  <p className="text-2xl font-bold text-gray-900">{totalMedals.secondPlace}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg shadow p-6">
              <div className="flex items-center">
                <Medal className="h-10 w-10 text-amber-700 mr-3" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    {getTranslatedText('Bronze Medals', 'Bronsemedaljer', 'வெண்கலப் பதக்கங்கள்')}
                  </p>
                  <p className="text-2xl font-bold text-amber-900">{totalMedals.thirdPlace}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg shadow p-6">
              <div className="flex items-center">
                <Award className="h-10 w-10 text-blue-500 mr-3" />
                <div>
                  <p className="text-sm font-medium text-blue-800">
                    {getTranslatedText('Participation', 'Deltakelse', 'பங்கேற்பு')}
                  </p>
                  <p className="text-2xl font-bold text-blue-900">{totalMedals.participation}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg shadow p-6">
              <div className="flex items-center">
                <Trophy className="h-10 w-10 text-purple-500 mr-3" />
                <div>
                  <p className="text-sm font-medium text-purple-800">
                    {getTranslatedText('Champion Trophies', 'Mestertrofeer', 'சாம்பியன் கோப்பைகள்')}
                  </p>
                  <p className="text-2xl font-bold text-purple-900">{totalMedals.champions}</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Category Summaries */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
              <div className="flex items-center">
                <Layers className="h-5 w-5 text-blue-500 mr-2" />
                <h2 className="text-lg font-medium text-gray-900">
                  {getTranslatedText(
                    'Medal Requirements by Category',
                    'Medaljebehov etter kategori',
                    'பிரிவு வாரியாக பதக்க தேவைகள்'
                  )}
                </h2>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {getTranslatedText('Category', 'Kategori', 'பிரிவு')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {getTranslatedText('Events', 'Hendelser', 'நிகழ்வுகள்')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {getTranslatedText('Gold Medals', 'Gullmedaljer', 'தங்கப் பதக்கங்கள்')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {getTranslatedText('Silver Medals', 'Sølvmedaljer', 'வெள்ளிப் பதக்கங்கள்')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {getTranslatedText('Bronze Medals', 'Bronsemedaljer', 'வெண்கலப் பதக்கங்கள்')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {getTranslatedText('Participation', 'Deltakelse', 'பங்கேற்பு')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {getTranslatedText('Total', 'Totalt', 'மொத்தம்')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {categorySummaries.map((category) => (
                    <tr key={category.categoryId}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {category.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {category.eventCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          {category.firstPlaceMedals}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                          {category.secondPlaceMedals}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-amber-100 text-amber-800">
                          {category.thirdPlaceMedals}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {category.participationMedals}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {category.firstPlaceMedals + category.secondPlaceMedals + category.thirdPlaceMedals + category.participationMedals}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Age Group Summaries */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
              <div className="flex items-center">
                <Calendar className="h-5 w-5 text-blue-500 mr-2" />
                <h2 className="text-lg font-medium text-gray-900">
                  {getTranslatedText(
                    'Medal Requirements by Age Group',
                    'Medaljebehov etter aldersgruppe',
                    'வயது பிரிவு வாரியாக பதக்க தேவைகள்'
                  )}
                </h2>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {getTranslatedText('Age Group', 'Aldersgruppe', 'வயது பிரிவு')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {getTranslatedText('Events', 'Hendelser', 'நிகழ்வுகள்')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {getTranslatedText('Gold Medals', 'Gullmedaljer', 'தங்கப் பதக்கங்கள்')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {getTranslatedText('Silver Medals', 'Sølvmedaljer', 'வெள்ளிப் பதக்கங்கள்')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {getTranslatedText('Bronze Medals', 'Bronsemedaljer', 'வெண்கலப் பதக்கங்கள்')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {getTranslatedText('Participation', 'Deltakelse', 'பங்கேற்பு')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {getTranslatedText('Total', 'Totalt', 'மொத்தம்')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {ageGroupSummaries.map((ageGroup) => (
                    <tr key={ageGroup.ageGroupId}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {ageGroup.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {ageGroup.eventCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          {ageGroup.firstPlaceMedals}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                          {ageGroup.secondPlaceMedals}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-amber-100 text-amber-800">
                          {ageGroup.thirdPlaceMedals}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {ageGroup.participationMedals}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {ageGroup.firstPlaceMedals + ageGroup.secondPlaceMedals + ageGroup.thirdPlaceMedals + ageGroup.participationMedals}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Champion Trophies */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
              <div className="flex items-center">
                <Trophy className="h-5 w-5 text-purple-500 mr-2" />
                <h2 className="text-lg font-medium text-gray-900">
                  {getTranslatedText(
                    'Champion Trophy Requirements',
                    'Krav til mestertrofeer',
                    'சாம்பியன் கோப்பை தேவைகள்'
                  )}
                </h2>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {getTranslatedText('Age Group', 'Aldersgruppe', 'வயது பிரிவு')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {getTranslatedText('Gender', 'Kjønn', 'பாலினம்')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {getTranslatedText('Champion Trophies', 'Mestertrofeer', 'சாம்பியன் கோப்பைகள்')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {championRequirements.map((champion, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {champion.ageGroup}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {getTranslatedGender(champion.gender)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                          {champion.count}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Detailed Requirements */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
              <div className="flex items-center">
                <Users className="h-5 w-5 text-blue-500 mr-2" />
                <h2 className="text-lg font-medium text-gray-900">
                  {getTranslatedText(
                    'Detailed Medal Requirements',
                    'Detaljerte medaljekrav',
                    'விரிவான பதக்க தேவைகள்'
                  )}
                </h2>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {getTranslatedText('Category', 'Kategori', 'பிரிவு')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {getTranslatedText('Age Group', 'Aldersgruppe', 'வயது பிரிவு')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {getTranslatedText('Gender', 'Kjønn', 'பாலினம்')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {getTranslatedText('Participants', 'Deltakere', 'பங்கேற்பாளர்கள்')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {getTranslatedText('Gold', 'Gull', 'தங்கம்')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {getTranslatedText('Silver', 'Sølv', 'வெள்ளி')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {getTranslatedText('Bronze', 'Bronse', 'வெண்கலம்')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {getTranslatedText('Participation', 'Deltakelse', 'பங்கேற்பு')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {medalRequirements.map((req, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {req.category}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {req.ageGroup}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {getTranslatedGender(req.gender)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {req.participantCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          {req.firstPlaceMedals}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                          {req.secondPlaceMedals}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-amber-100 text-amber-800">
                          {req.thirdPlaceMedals}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {req.participationMedals}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MedalRequirementsPage;